
import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Globe, Mail, Activity, AlertTriangle, Clock, Database, Shield } from 'lucide-react'
import { useMissingDomains } from '@/hooks/useMissingDomains'
import { useMailboxes } from '@/hooks/useMailboxes'
import { PRIMARY_DOMAINS } from '@/lib/primary-domains'

interface DomainMetricsProps {
  domains: any[]
  onRenewalFilterClick?: (filterType: 'renewing' | 'expiring') => void
  onNoMailboxFilterClick?: () => void
  onActiveStatusFilterClick?: (status: 'active' | 'non-active') => void
}

export function DomainMetrics({ domains, onRenewalFilterClick, onNoMailboxFilterClick, onActiveStatusFilterClick }: DomainMetricsProps) {
  const { data: missingDomains = [] } = useMissingDomains()
  const { data: allMailboxes = [] } = useMailboxes()
  const [showMissingDomainsModal, setShowMissingDomainsModal] = useState(false)
  const [showPrimaryDomainsModal, setShowPrimaryDomainsModal] = useState(false)
  const [showNoMailboxesModal, setShowNoMailboxesModal] = useState(false)
  const [showExpiringModal, setShowExpiringModal] = useState(false)

  const totalDomains = domains.length
  const activeDomains = domains.filter(d => d.status === 'Active').length

  // Active mailboxes from mailboxes table (source of truth) — not from domains table aggregate
  const activeMailboxes = allMailboxes.filter(m => m.status !== 'DEACTIVATED').length
  const deactivatedMailboxes = allMailboxes.filter(m => m.status === 'DEACTIVATED').length

  // Domains expiring in next 30 days
  const thirtyDaysFromNow = new Date()
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
  const expiringDomainsList = domains.filter(d => {
    if (!d.expiry_date) return false
    if (d.status !== 'Active') return false
    const expiryDate = new Date(d.expiry_date)
    return expiryDate <= thirtyDaysFromNow && expiryDate >= new Date()
  })
  const expiringNext30Days = expiringDomainsList.length

  // Active domains with no mailboxes
  const noMailboxDomainsList = domains.filter(d => d.status === 'Active' && d.total_mailboxes === 0)
  const activeDomainsNoMailboxes = noMailboxDomainsList.length

  // Non-active domains
  const nonActiveDomains = domains.filter(d => d.status !== 'Active').length

  // Registrar breakdown (active only)
  const activeSpaceship = domains.filter(d => d.status === 'Active' && d.domain_registrar === 'Spaceship').length
  const activeInboxKit = domains.filter(d => d.status === 'Active' && d.domain_registrar === 'InboxKit').length

  // Mailbox provider breakdown (active mailboxes only, on active domains)
  const activeDomainsList = domains.filter(d => d.status === 'Active')
  const cheapinboxesMailboxes = activeDomainsList
    .filter(d => d.mailbox_provider === 'Cheapinboxes')
    .reduce((sum, d) => sum + (d.active_mailboxes || 0), 0)
  const inboxkitMailboxes = activeDomainsList
    .filter(d => d.mailbox_provider === 'InboxKit')
    .reduce((sum, d) => sum + (d.active_mailboxes || 0), 0)

  // Primary domains (domains in PRIMARY_DOMAINS list)
  const primaryDomainsList = domains.filter(d => {
    const domainName = d.domain?.toLowerCase()
    return domainName && PRIMARY_DOMAINS.includes(domainName as any)
  })
  const activePrimaryDomains = primaryDomainsList.filter(d => d.status === 'Active')
  const deactivatedPrimaryDomains = primaryDomainsList.filter(d => d.status !== 'Active')
  const primaryDomainsCount = activePrimaryDomains.length

  // Mailbox provider breakdown from mailboxes table (source of truth)
  const domainProviderMap = new Map(domains.map(d => [d.domain?.toLowerCase(), d.mailbox_provider]))
  const activeMailboxesList = allMailboxes.filter(m => m.status !== 'DEACTIVATED')
  const cheapinboxesActiveMailboxes = activeMailboxesList.filter(m => {
    const provider = domainProviderMap.get(m.domain?.toLowerCase())
    return provider === 'Cheapinboxes'
  }).length
  const inboxkitActiveMailboxes = activeMailboxesList.filter(m => {
    const provider = domainProviderMap.get(m.domain?.toLowerCase())
    return provider === 'InboxKit'
  }).length

  // Missing domains metrics
  const totalMissingDomains = missingDomains.length
  const totalMissingMailboxes = missingDomains.reduce((sum, d) => sum + d.mailbox_count, 0)

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Domain Infrastructure */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Globe className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-xl font-semibold">Domain Infrastructure</CardTitle>
          </div>
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <Activity className="h-3 w-3 mr-1" />
            Live
          </Badge>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Headline Numbers */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-5 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">{activeDomains}</div>
                </div>
                <div className="text-sm font-semibold text-foreground">Active Domains</div>
                <div className="text-xs text-muted-foreground mt-1">{activeSpaceship} Spaceship · {activeInboxKit} InboxKit</div>
              </div>
            </div>
            <div className="p-5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Mail className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{activeMailboxes}</div>
                </div>
                <div className="text-sm font-semibold text-foreground">Active Mailboxes</div>
                <div className="text-xs text-muted-foreground mt-1">{cheapinboxesActiveMailboxes} Cheapinboxes · {inboxkitActiveMailboxes} InboxKit</div>
              </div>
            </div>
          </div>

          {/* Domain Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                <Globe className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-base font-semibold text-foreground">Domain Details</h3>
            </div>
            <div className="grid grid-cols-5 gap-4">
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                      <Shield className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <button
                      onClick={() => setShowPrimaryDomainsModal(true)}
                      className="text-3xl font-bold transition-all text-foreground cursor-pointer hover:text-blue-600"
                    >
                      {primaryDomainsCount}
                    </button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Primary Domains</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">
                    {deactivatedPrimaryDomains.length > 0
                      ? `${deactivatedPrimaryDomains.length} deactivated`
                      : 'Business domains'}
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                      <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                    </div>
                    <button
                      onClick={() => setShowNoMailboxesModal(true)}
                      className="text-3xl font-bold transition-all text-foreground cursor-pointer hover:text-amber-600"
                    >
                      {activeDomainsNoMailboxes}
                    </button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">No Mailboxes</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">Active domains</div>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-rose-100 dark:bg-rose-900/30">
                      <Activity className="h-3 w-3 text-rose-600 dark:text-rose-400" />
                    </div>
                    <button
                      onClick={() => onActiveStatusFilterClick?.('non-active')}
                      className="text-3xl font-bold transition-all text-foreground cursor-pointer hover:text-rose-600"
                    >
                      {nonActiveDomains}
                    </button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Deactivated</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">{totalDomains} total domains</div>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                      <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                    </div>
                    <button
                      onClick={() => setShowExpiringModal(true)}
                      className="text-3xl font-bold transition-all text-foreground cursor-pointer hover:text-amber-600"
                    >
                      {expiringNext30Days}
                    </button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Expiring</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">In next 30 days</div>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-900/30">
                      <Database className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                    </div>
                    <button
                      onClick={() => setShowMissingDomainsModal(true)}
                      className="text-3xl font-bold transition-all text-foreground cursor-pointer hover:text-slate-600"
                    >
                      {totalMissingDomains}
                    </button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Missing</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">{totalMissingMailboxes} untracked</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary Domains Modal */}
      <Dialog open={showPrimaryDomainsModal} onOpenChange={setShowPrimaryDomainsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Primary Domains ({primaryDomainsCount})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Business domains that don't require warmup functionality.
            </p>
            {primaryDomainsList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No primary domains found.
              </div>
            ) : (
              <div className="space-y-3">
                {activePrimaryDomains.length > 0 && (
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active ({activePrimaryDomains.length})</div>
                )}
                {activePrimaryDomains.map((domain) => (
                  <div key={domain.domain} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-blue-600" />
                        <h4 className="font-semibold">{domain.domain}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default">{domain.status}</Badge>
                        <Badge variant="secondary">
                          {domain.total_mailboxes || 0} mailbox{(domain.total_mailboxes || 0) !== 1 ? 'es' : ''}
                        </Badge>
                      </div>
                    </div>
                    {domain.client_name && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Client: {domain.client_name}
                      </div>
                    )}
                  </div>
                ))}
                {deactivatedPrimaryDomains.length > 0 && (
                  <>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-4">Deactivated ({deactivatedPrimaryDomains.length})</div>
                    {deactivatedPrimaryDomains.map((domain) => (
                      <div key={domain.domain} className="border rounded-lg p-4 opacity-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <h4 className="font-semibold text-muted-foreground">{domain.domain}</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{domain.status}</Badge>
                            <Badge variant="secondary">
                              {domain.total_mailboxes || 0} mailbox{(domain.total_mailboxes || 0) !== 1 ? 'es' : ''}
                            </Badge>
                          </div>
                        </div>
                        {domain.client_name && (
                          <div className="text-xs text-muted-foreground mt-2">
                            Client: {domain.client_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* No Mailboxes Modal */}
      <Dialog open={showNoMailboxesModal} onOpenChange={setShowNoMailboxesModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Active Domains with No Mailboxes ({activeDomainsNoMailboxes})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              These active domains have no mailboxes configured.
            </p>
            {noMailboxDomainsList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                All active domains have mailboxes configured.
              </div>
            ) : (
              <div className="space-y-3">
                {noMailboxDomainsList.map((domain) => (
                  <div key={domain.domain} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-amber-600" />
                        <h4 className="font-semibold">{domain.domain}</h4>
                      </div>
                      <Badge variant="secondary">
                        {domain.domain_registrar || 'Unknown registrar'}
                      </Badge>
                    </div>
                    {domain.expiry_date && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Expires: {new Date(domain.expiry_date).toLocaleDateString()}
                        {domain.mailbox_provider && ` · Provider: ${domain.mailbox_provider}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Expiring Domains Modal */}
      <Dialog open={showExpiringModal} onOpenChange={setShowExpiringModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Expiring Domains ({expiringNext30Days})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Active domains expiring within the next 30 days.
            </p>
            {expiringDomainsList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No domains expiring in the next 30 days.
              </div>
            ) : (
              <div className="space-y-3">
                {expiringDomainsList.map((domain) => {
                  const expiryDate = new Date(domain.expiry_date)
                  const daysLeft = Math.ceil((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                  return (
                    <div key={domain.domain} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-amber-600" />
                          <h4 className="font-semibold">{domain.domain}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={daysLeft <= 7 ? 'destructive' : 'secondary'} className={daysLeft <= 7 ? '' : 'bg-amber-100 text-amber-700'}>
                            {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                          </Badge>
                          <Badge variant="secondary">
                            {domain.total_mailboxes || 0} mailbox{(domain.total_mailboxes || 0) !== 1 ? 'es' : ''}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        Expires: {expiryDate.toLocaleDateString()}
                        {domain.domain_registrar && ` · Registrar: ${domain.domain_registrar}`}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Missing Domains Modal */}
      <Dialog open={showMissingDomainsModal} onOpenChange={setShowMissingDomainsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              Missing Domains ({totalMissingDomains})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              These domains have mailboxes in the system but are not tracked in the domains table.
            </p>

            {missingDomains.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No missing domains found. All mailboxes are properly tracked.
              </div>
            ) : (
              <div className="space-y-4">
                {missingDomains.map((domain, index) => (
                  <div key={domain.domain} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-lg">{domain.domain}</h4>
                      <Badge variant="secondary">
                        {domain.mailbox_count} mailbox{domain.mailbox_count !== 1 ? 'es' : ''}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-muted-foreground">Untracked Mailboxes:</h5>
                      <div className="grid grid-cols-1 gap-2">
                        {domain.mailboxes.map((email, emailIndex) => (
                          <div
                            key={emailIndex}
                            className="flex items-center gap-2 p-2 bg-muted rounded text-sm"
                          >
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {email}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
